import { computed } from 'vue';
import { parseAreaSwitchBandState } from '~/types/areaSwitchBand';
import type { AreaSwitchBandState } from '~/types/areaSwitchBand';
import { COOKIE_NAME_AREA_SWITCH_BAND } from '~/types/cookieNames';
import { applyBackLinkClick, applyCloseClick, applyPageView } from '~/utils/areaSwitchBandTransitions';
import { findSectionConfigForPath } from '~/utils/findSectionConfigForPath';
import { isCheckoutPath } from '~/utils/isCheckoutPath';
import { parseLocaleFromPath } from '~/utils/parseLocaleFromPath';
import { resolveAreaSwitchBandBackUrl } from '~/utils/resolveAreaSwitchBandBackUrl';

const STATE_KEY = 'area-switch-band';

/**
 * The cookie is user input. The default decoder of `useCookie` throws an error for a malformed percent escape,
 * and the visitor then gets an error page. Thus a value that does not decode is "no state".
 */
const decodeCookieValue = (value: string | null | undefined): string | undefined => {
    if (!value) return undefined;

    try {
        return decodeURIComponent(value);
    } catch {
        return undefined;
    }
};

/**
 * Reads the cookie of the browser. Another tab can change it at any time. A `useCookie` ref gets such a change
 * only after an event (or never, in a browser without `cookieStore` and `BroadcastChannel`). The browser cookie
 * is always current.
 */
const readBrowserCookie = (): string | undefined => {
    const prefix = `${COOKIE_NAME_AREA_SWITCH_BAND}=`;
    const entry = document.cookie.split('; ').find((item) => item.startsWith(prefix));

    return entry ? decodeCookieValue(entry.slice(prefix.length)) : undefined;
};

/** The cookie value is the JSON string of the state. The default encoder of `useCookie` would encode it twice. */
const encodeCookieValue = (value: string | undefined): string => encodeURIComponent(value ?? '');

/** A session cookie: it has no `maxAge` and no `expires`. */
const COOKIE_OPTIONS = {
    path: '/',
    sameSite: 'lax',
    secure: true,
    decode: decodeCookieValue,
    encode: encodeCookieValue
} as const;

export interface AreaSwitchBand {
    /**
     * The key of the translation with the full text, for example `platform.backTo.root` for "Zurück zur Health Family".
     * Each area has its own key, because the grammar is different per area ("zur", "zum").
     */
    readonly labelKey: string;
    /** The URL of the back link. It is always a path in the current locale. */
    readonly backUrl: string;
}

/**
 * The state of the area switch band. Call it in the setup of a plugin or a component, not in a callback.
 *
 * - The state is in `useState`. It is the source of truth in the request and for the rendering.
 * - The session cookie stores the state. The server reads it, and the browser keeps it for the next page load.
 *   Each call creates its own `useCookie` ref, and only an action that changes the state writes to it.
 * - All tabs share the cookie. In the browser, each action reads the cookie again before it changes the state,
 *   so that a tab with an old state does not overwrite the changes of another tab.
 */
export function useAreaSwitchBand() {
    const nuxtApp = useNuxtApp();
    const countryLanguages = useCountryLanguages();
    const currentRoute = useRoute();
    const cookie = useCookie<string | undefined>(COOKIE_NAME_AREA_SWITCH_BAND, COOKIE_OPTIONS);
    const state = useState<AreaSwitchBandState | null>(STATE_KEY, () => parseAreaSwitchBandState(cookie.value));

    const readSharedState = () => {
        if (import.meta.client) state.value = parseAreaSwitchBandState(readBrowserCookie());
    };

    const commit = (nextState: AreaSwitchBandState | null) => {
        state.value = nextState;
        cookie.value = nextState ? JSON.stringify(nextState) : undefined;
    };

    /** `null` when no band is visible. The band is never visible on a checkout page. */
    const visibleBand = computed<AreaSwitchBand | null>(() => {
        const band = state.value?.band;
        if (!band || isCheckoutPath(currentRoute.path)) return null;

        const backUrl = resolveAreaSwitchBandBackUrl(state.value, nuxtApp.$subSectionRegistry?.value, countryLanguages);
        if (!backUrl) return null;

        return { labelKey: `platform.backTo.${band.area}`, backUrl };
    });

    /** Records a confirmed page view. It ignores a page that has no area, and a checkout page. */
    const recordPageView = (route: { readonly path: string; readonly fullPath: string }) => {
        const config = findSectionConfigForPath(nuxtApp.$subSectionRegistry?.value, route.path, countryLanguages);
        const locale = parseLocaleFromPath(route.path);
        if (!config || !locale?.country || !locale.language) return;

        readSharedState();
        commit(
            applyPageView(state.value, {
                area: config.sectionId,
                localeSegment: `${locale.country}-${locale.language}`,
                fullPath: route.fullPath
            })
        );
    };

    /** Hides the band until the next area switch. */
    const close = () => {
        readSharedState();
        commit(applyCloseClick(state.value));
    };

    /**
     * Call it when the user clicks the back link. The area you go back to does not show a band.
     *
     * The state changes immediately, and `useCookie` writes the cookie of the browser in a watcher that runs in
     * a microtask. The browser starts a navigation, also a full page load, only after this microtask. Thus
     * the cookie is already written when the navigation starts.
     */
    const returnToPreviousArea = () => {
        readSharedState();
        if (!visibleBand.value) return;

        commit(applyBackLinkClick(state.value, visibleBand.value.backUrl));
    };

    return { visibleBand, recordPageView, close, returnToPreviousArea };
}
